import { replaceWords } from "../services/filter-value";

(() => {
  const constants = {
    UPSELL_CART_UPDATE_EVENT: 'lcu:smart-cart:updated',
    PROTECTION_ITEM_WORDS: ['protection'],
    CART_UPDATED: 'upsell:cart-updated',
    CART_DRAWER_SECTION: '/?section_id=cart-drawer',
    FINAL_SALE_CLASS: 'cart-item__final-sale',
  }

  const elementSelectors = {
    wrapper: ".lcu__drawer__cart-items",
    line: ".lcu__drawer__item",
    quantityEl: ".lcu__drawer__input-wrapper",
    title: ".lcu__drawer__item-title",
    prices: ".lcu__product-prices",
    bubble: "[data-cart-indicator]",
    themeLine: ".cart__items--row",
    themeVariantLink: 'a[href*="variant="]',
  }

  const updateItemTitles = (items, titleSelector) => {
    items?.forEach((item) => {
      const titleEl = item.querySelector(titleSelector);
      if (titleEl) {
        const updatedTitle = replaceWords(titleEl.textContent);
        titleEl.textContent = updatedTitle;

        if (constants.PROTECTION_ITEM_WORDS.some(word => updatedTitle.toLowerCase().includes(word))) {
          const quantityEl = item.querySelector(elementSelectors.quantityEl);
          quantityEl?.remove();
        }
      }
    });
  }

  // Llama Cart renders its own line items, so snippets/final-sale.liquid never
  // runs for them. Instead of restating its rules here — product type, the
  // promo gift card case, the 40% threshold — render the theme's own cart
  // drawer for the same cart and read back which variants it flagged. One
  // source of truth, and this keeps following it when it changes.
  const fetchFinalSaleVariantIds = async () => {
    const variantIds = new Set();

    try {
      const response = await fetch(constants.CART_DRAWER_SECTION, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return variantIds;

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');

      markup.querySelectorAll(`.${constants.FINAL_SALE_CLASS}`).forEach((badge) => {
        const href = badge
          .closest(elementSelectors.themeLine)
          ?.querySelector(elementSelectors.themeVariantLink)
          ?.getAttribute('href');
        if (!href) return;

        const variantId = new URLSearchParams(href.split('?')[1]).get('variant');
        if (variantId) variantIds.add(variantId);
      });
    } catch (error) {
      console.error(`Error: Unable to resolve final sale items. ${error.message}`);
    }

    return variantIds;
  }

  const renderFinalSaleLabels = async (items) => {
    if (!items?.length) return;
    const finalSaleVariantIds = await fetchFinalSaleVariantIds();

    items.forEach((item) => {
      const existingLabel = item.querySelector(`.${constants.FINAL_SALE_CLASS}`);

      // The app re-renders rows in place, so a row that is no longer final sale
      // has to lose the label it was given on an earlier pass.
      if (!finalSaleVariantIds.has(item.dataset.variantId)) {
        existingLabel?.remove();
        return;
      }
      if (existingLabel) return;

      const label = document.createElement('span');
      label.className = constants.FINAL_SALE_CLASS;
      label.textContent = 'Final Sale';
      item.querySelector(elementSelectors.prices)?.append(label);
    });
  }

  const getCartItems = () =>
    document.querySelector(elementSelectors.wrapper)?.querySelectorAll(elementSelectors.line);

  const onCartUpdated = () => {
    const cartItems = getCartItems();
    updateItemTitles(cartItems, elementSelectors.title);
    renderFinalSaleLabels(cartItems);
  }

  const onProductAdded = (event) => {
    const { item_count: itemsCount } = event.detail.newCart;
    document.querySelectorAll(elementSelectors.bubble)?.forEach(indicator => {
      indicator.textContent = itemsCount;
    });
  }

  // The app builds the whole drawer client-side — none of it is in the server
  // HTML — so there is nothing to label at DOMContentLoaded. Wait for the list
  // to appear rather than guessing at a delay, then stop watching: the update
  // event covers every change after the first render.
  const observeFirstRender = () => {
    if (document.querySelector(elementSelectors.wrapper)) {
      renderFinalSaleLabels(getCartItems());
      return;
    }

    const observer = new MutationObserver(() => {
      if (!document.querySelector(elementSelectors.wrapper)) return;
      observer.disconnect();
      renderFinalSaleLabels(getCartItems());
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  const onDocumentDownloaded = () => {
    document.addEventListener(constants.UPSELL_CART_UPDATE_EVENT, onCartUpdated);
    document.addEventListener(constants.CART_UPDATED, onProductAdded);
    observeFirstRender();
  }

  document.addEventListener('DOMContentLoaded', onDocumentDownloaded);
})();
